import { useCallback, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  getGetTicketsForTravellerQueryKey,
  getGetTravellerBearersQueryKey,
  getGetTravellerByIDQueryKey,
  useTravellerGlobalSearch,
  useTravellerReplaceSchoolCard,
} from '@/api/trafikportalen';
import { WarningBanner } from '@/components/core/WarningBanner';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import { Spinner } from '@/components/core/Spinner';
import { FormElement } from '@/components/FormElement';
import { FormSection } from '@/components/FormSection';
import { getApiErrorMessage } from '@/helpers/apiError';
import { CARD_SERIAL_LENGTH, normalizeCardSerial } from '@/helpers/cardSerial';
import { formatCardNumber } from '@/helpers/formatting';
import { getTravellerSearchCardKind } from '@/helpers/travellerSearch';
import { useClaimableCardSerial } from '@/hooks/useClaimableCardSerial';
import { toast } from '@/hooks/useToast';
import { IconCheck } from '@/icons';

interface ReplaceSchoolCardFormProps {
  travellerId: string;
  currentSerial: string;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const isTravellerSearchQuery = (queryKey: readonly unknown[]): boolean => {
  const [path, params] = queryKey;
  return (
    path === '/travellers' &&
    typeof params === 'object' &&
    params !== null &&
    'q' in params
  );
};

export const ReplaceSchoolCardForm = ({
  travellerId,
  currentSerial,
  onOpenChange,
  onSuccess,
}: ReplaceSchoolCardFormProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const replaceSchoolCard = useTravellerReplaceSchoolCard();
  const cardSerial = useClaimableCardSerial();
  const serialInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    serialInputRef.current?.focus();
  }, []);

  const isComplete = cardSerial.serial.length === CARD_SERIAL_LENGTH;
  const isCurrentCard =
    isComplete && cardSerial.serial === normalizeCardSerial(currentSerial);

  const {
    data: searchResults,
    isFetching: isSearching,
    isError: searchFailed,
  } = useTravellerGlobalSearch(
    { q: cardSerial.serial },
    {
      query: {
        enabled: isComplete && !isCurrentCard,
        retry: false,
      },
    },
  );

  const cardKind = useMemo(
    () =>
      isComplete
        ? getTravellerSearchCardKind(searchResults ?? [], cardSerial.serial)
        : undefined,
    [cardSerial.serial, isComplete, searchResults],
  );

  const isCheckingKind = isComplete && !isCurrentCard && isSearching;
  const kindFailed =
    isComplete && !isCurrentCard && searchFailed && !isSearching;

  let kindError = '';
  if (isCurrentCard) {
    kindError = formatMessage({
      defaultMessage:
        'Det är det nuvarande skolkortet. Ange kortnumret på det nya kortet.',
    });
  } else if (kindFailed) {
    kindError = formatMessage({
      defaultMessage: 'Kunde inte kontrollera om kortet är ett skolkort',
    });
  } else if (cardKind === 'travelcard' || cardKind === 'walletcard') {
    kindError = formatMessage({
      defaultMessage: 'Kortet är inte ett skolkort',
    });
  } else if (cardKind === 'schoolcard') {
    kindError = formatMessage({
      defaultMessage: 'Kortet är redan ett skolkort på en resenär',
    });
  }

  const errorMessage = kindError || cardSerial.errorMessage;
  const isChecking = cardSerial.isChecking || isCheckingKind;
  const canSubmit =
    cardSerial.isClaimable &&
    !kindError &&
    !isCheckingKind &&
    !replaceSchoolCard.isPending;

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit) return;

      try {
        await replaceSchoolCard.mutateAsync({
          travellerID: travellerId,
          data: { card_serial: cardSerial.serial },
        });

        toast.success(
          {
            title: formatMessage({
              defaultMessage: 'Skolkortet är bytt',
            }),
          },
          { autoClose: 3000 },
        );

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetTravellerBearersQueryKey(travellerId),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetTravellerByIDQueryKey(travellerId),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetTicketsForTravellerQueryKey(travellerId),
          }),
          queryClient.invalidateQueries({
            predicate: (query) => isTravellerSearchQuery(query.queryKey),
          }),
        ]);

        onSuccess?.();
        onOpenChange(false);
      } catch (error) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: replaceSchoolCardErrorMessage(error, formatMessage),
          },
          { autoClose: 3000 },
        );
      }
    },
    [
      canSubmit,
      cardSerial.serial,
      formatMessage,
      onOpenChange,
      onSuccess,
      queryClient,
      replaceSchoolCard,
      travellerId,
    ],
  );

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <WarningBanner>
        <FormattedMessage
          defaultMessage="Skolkortet {serial} byts mot ett ledigt kort. Aktiva och kommande biljetter flyttas till det nya kortet. Det gamla kortet tas bort."
          values={{
            serial: (
              <span className="font-mono">
                {formatCardNumber(currentSerial)}
              </span>
            ),
          }}
        />
      </WarningBanner>
      <FormSection editMode>
        <FormElement
          id="replacement_card_serial"
          required
          label={<FormattedMessage defaultMessage="Nytt kortnummer" />}
          input={
            <Input
              id="replacement_card_serial"
              ref={serialInputRef}
              name="card_serial"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              required
              placeholder={formatMessage({
                defaultMessage: '1234 1234 1234 1234',
              })}
              value={cardSerial.formattedSerial}
              onChange={cardSerial.handleChange}
              error={errorMessage}
              helperText={
                errorMessage || isChecking || cardSerial.isClaimable
                  ? undefined
                  : formatMessage({
                      defaultMessage: '16 siffror på ett ledigt kort.',
                    })
              }
              helperTextReserveSpace
              aria-busy={isChecking || undefined}
              aria-describedby={
                canSubmit ? 'replacement_card_serial-status' : undefined
              }
              endAdornment={
                isChecking ? (
                  <Spinner className="size-4" />
                ) : canSubmit ? (
                  <IconCheck
                    className="size-4 text-green-600"
                    aria-hidden="true"
                  />
                ) : null
              }
            />
          }
        />
        {canSubmit && (
          <p
            id="replacement_card_serial-status"
            role="status"
            className="text-text-800"
          >
            <FormattedMessage defaultMessage="Kortet är ledigt och kan användas som nytt skolkort." />
          </p>
        )}
      </FormSection>
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={handleCancel}>
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button
          type="submit"
          variant="filled"
          startIcon={<IconCheck />}
          loading={replaceSchoolCard.isPending}
          disabled={!canSubmit}
        >
          <FormattedMessage defaultMessage="Byt skolkort" />
        </Button>
      </div>
    </form>
  );
};

const replaceSchoolCardErrorMessage = (
  error: unknown,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string => {
  if (isAxiosError(error)) {
    if (error.response?.status === 409) {
      return formatMessage({
        defaultMessage:
          'Skolkortet kan inte bytas. Resenären har inget enskilt utbytbart skolkort, eller så pågår redan ett kortbyte.',
      });
    }
    if (error.response?.status === 404) {
      return formatMessage({
        defaultMessage: 'Resenären har inga kort att byta.',
      });
    }
  }

  return getApiErrorMessage(
    error,
    formatMessage({ defaultMessage: 'Ett fel inträffade' }),
  );
};
